import React from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { INCREASE_LIMITS_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { GT_LEVEL } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { AML_LEVEL_BASIC } from 'apps/auth/constants/Constants';
import { ISRAEL } from 'apps/auth/constants/WellKnownCountries';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

const IncreaseLimitButton = (props) => {
    const { kycGaCheckInProcess, profile_form, aml_gt_increase_level_link, currentLimitLevel, onfidoTooManyAttempts, onfidoGtInProgress, onfidoCheckError } =
        useExtra();
    const {
        limitIncreaseFailed,
        viewDetails,
        gotIt,
        limitsReviewingDocument,
        gaLimitProcessButton,
        upgradeLimitButton,
        limitViewDetailsTryAgain,
        thirdAttemptTitle,
        reviewNewSubtitle,
        reviewTooManyAttemptsSubtitle1,
        reviewTooManyAttemptsSubtitle2,
        contactSupport,
    } = usePageMessages();

    const { countryId } = useAuth();

    const canSendDocuments = (gtInProcess, gaInProcess) => !gtInProcess && !gaInProcess;
    const expectedGTLevel = currentLimitLevel + 1;
    const isThreeMonthLimits = profile_form?.threeMonthLimitEnabled;
    const processButtonTitle = isThreeMonthLimits ? upgradeLimitButton : gaLimitProcessButton;

    const canShowLimitProcessButton = (countryId = 0, currentLimitLevel = 0) => {
        if (countryId === ISRAEL && currentLimitLevel > AML_LEVEL_BASIC) {
            return false;
        }
        return !isThreeMonthLimits ? canSendDocuments(onfidoGtInProgress, kycGaCheckInProcess) : true;
    };

    if (onfidoTooManyAttempts) {
        return (
            <Row indent="adaptive-32-48">
                <Row flex align="center" indent="8px">
                    <Icon name="clock" size="24px" rightIndent="16px" />
                    <div>{limitsReviewingDocument}</div>
                </Row>
                <Button
                    type="link"
                    size="tight"
                    variant="row"
                    caption={viewDetails}
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.toggleModal(
                            true,
                            <ModalComponent canCloseModal={false}>
                                <InfoPage
                                    buttonRowCaption={contactSupport}
                                    buttonRowType="link"
                                    buttonRowOnClick={() => {
                                        props.toggleChat(true);
                                    }}
                                    iconName="id-card-search-80"
                                    iconColor="primary"
                                    headerText={thirdAttemptTitle}
                                    secondaryText={
                                        <>
                                            <p>{reviewTooManyAttemptsSubtitle1}</p>
                                            <p>{reviewTooManyAttemptsSubtitle2}</p>
                                        </>
                                    }
                                    primaryButtonCaption={gotIt}
                                    primaryButtonType="link"
                                    primaryButtonOnClick={() => {
                                        props.toggleModal(false);
                                    }}
                                    insideModalComponent
                                />
                            </ModalComponent>,
                        );
                    }}
                />
            </Row>
        );
    } else if (onfidoCheckError) {
        return (
            <Row indent="adaptive-32-48">
                <Row flex align="center" indent="8px">
                    <Icon name="cross-rounded" color="primary" size="24px" rightIndent="16px" />
                    <div>{limitIncreaseFailed}</div>
                </Row>
                <Button type="link" size="tight" href={aml_gt_increase_level_link} variant="row" caption={limitViewDetailsTryAgain} negativeMargin />
            </Row>
        );
    } else if (!canSendDocuments(onfidoGtInProgress, kycGaCheckInProcess)) {
        return (
            <Row indent="adaptive-32-48">
                <Row flex align="center" indent="8px">
                    <Icon name="clock" size="24px" rightIndent="16px" />
                    <div>{limitsReviewingDocument}</div>
                </Row>
                <Button
                    type="link"
                    size="tight"
                    variant="row"
                    caption={viewDetails}
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.toggleModal(
                            true,
                            <ModalComponent canCloseModal={false}>
                                <InfoPage
                                    iconName="id-card-search-80"
                                    iconColor="primary"
                                    headerText={thirdAttemptTitle}
                                    secondaryText={<DangerouslyHtml dangerouslyHtml={reviewNewSubtitle} />}
                                    primaryButtonCaption={gotIt}
                                    primaryButtonType="link"
                                    primaryButtonOnClick={() => {
                                        props.toggleModal(false);
                                    }}
                                    insideModalComponent
                                />
                            </ModalComponent>,
                        );
                    }}
                />
            </Row>
        );
    } else if (canShowLimitProcessButton(countryId, profile_form?.limitLvl)) {
        return (
            <Panel
                type="reactLink"
                to={aml_gt_increase_level_link}
                onClick={() => {
                    amplitude.getInstance().logEvent(INCREASE_LIMITS_SHOWN, { [GT_LEVEL]: expectedGTLevel });
                }}
                isHoverable
                negativeMargin
            >
                <DiskBadgeWrapper>
                    <DiskBadge variant="secondary" iconName="arrow-up" />
                </DiskBadgeWrapper>
                <Text color="primary">{processButtonTitle}</Text>
            </Panel>
        );
    }
    return null;
};

export default connect(null, { toggleModal, toggleChat })(IncreaseLimitButton);
